import React, { useState, useRef } from 'react';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Eye,
  Heart,
  User,
  Mail,
  Calendar,
  Tag,
  Link as LinkIcon,
  Sparkles,
  ExternalLink,
  Clock,
  Upload,
  FileVideo,
  Loader2,
  Download,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  RefreshCw,
} from 'lucide-react';
import { AdminContentDetailDto, ContentStatus, ContentType, ContentSource } from '../../types/content';
import {
  cn,
  formatDateTime,
  getContentStatusLabel,
  getContentStatusBadgeVariant,
  getContentSourceLabel,
  getContentTypeLabel,
  formatVideoDuration,
} from '../../utils/helpers';
import ContentMediaPreview from './ContentMediaPreview';
import RejectReasonModal from './RejectReasonModal';

// ============================================================================
// Badge classes (mirrored from ContentList)
// ============================================================================

const badgeClasses: Record<string, string> = {
  default: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  success: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  warning: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  destructive: 'bg-red-500/15 text-red-400 border-red-500/30',
  secondary: 'bg-muted/50 text-muted-foreground border-border',
};

// ============================================================================
// Props
// ============================================================================

interface ContentDetailPanelProps {
  content: AdminContentDetailDto | null;
  loading: boolean;
  onApprove: (id: string) => Promise<boolean>;
  onRevert: (id: string, reason?: string) => Promise<boolean>;
  onReject: (id: string, reason?: string) => Promise<boolean>;
  onRegenerate?: (id: string, prompt?: string) => Promise<boolean>;
  onUploadTikTok: (tiktokVideoId: string, file: File) => Promise<boolean>;
  onImportTikTok: (tiktokVideoId: string) => Promise<boolean>;
  onClose: () => void;
  actionLoading: boolean;
  regenerateLoading?: boolean;
  uploadLoading: boolean;
  importLoading: boolean;
}

// ============================================================================
// Component
// ============================================================================

const ContentDetailPanel: React.FC<ContentDetailPanelProps> = ({
  content,
  loading,
  onApprove,
  onRevert,
  onReject,
  onRegenerate,
  onUploadTikTok,
  onImportTikTok,
  onClose,
  actionLoading,
  regenerateLoading = false,
  uploadLoading,
  importLoading,
}) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [regeneratePrompt, setRegeneratePrompt] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showManualUpload, setShowManualUpload] = useState(false);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center border-l border-border">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading content...</p>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="flex-1 flex items-center justify-center border-l border-border">
        <div className="text-center px-6">
          <Eye className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Select a content item to review</p>
        </div>
      </div>
    );
  }

  const canReview = content.status === ContentStatus.PENDING_REVIEW;
  const isPendingImport = content.status === ContentStatus.PENDING_IMPORT;
  const isReady = content.status === ContentStatus.READY;
  const badgeVariant = getContentStatusBadgeVariant(content.status);
  const isAiGenerated = content.source === ContentSource.AI_GENERATED || content.source === ContentSource.SORA;
  const canRegenerate = isAiGenerated && onRegenerate && content.serviceTag;

  const handleApprove = async () => {
    await onApprove(content.id);
  };

  const handleRevert = async (reason: string) => {
    const success = await onRevert(content.id, reason || undefined);
    if (success) {
      setShowRevertModal(false);
    }
  };

  const handleReject = async (reason: string) => {
    const success = await onReject(content.id, reason);
    if (success) {
      setShowRejectModal(false);
    }
  };

  const handleRegenerate = async () => {
    if (!onRegenerate) return;
    const promptToSend = regeneratePrompt.trim() || undefined;
    const success = await onRegenerate(content.id, promptToSend);
    if (success) setShowRegenerateConfirm(false);
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !content.socialImport) return;
    const success = await onUploadTikTok(content.socialImport.externalId, selectedFile);
    if (success) {
      setSelectedFile(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.type.startsWith('video/') || file.name.match(/\.(mp4|mov)$/i))) {
      handleFileSelect(file);
    }
  };

  const handleAutoImport = async () => {
    if (!content.socialImport) return;
    const success = await onImportTikTok(content.socialImport.externalId);
    if (success) {
      setSelectedFile(null);
      setShowManualUpload(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col border-l border-border min-w-0">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="lg:hidden p-1.5 rounded-lg hover:bg-muted/50 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-muted-foreground" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-foreground truncate max-w-[200px] sm:max-w-none">
                {content.caption || 'Untitled Content'}
              </h2>
              <span
                className={cn(
                  'text-xs font-medium px-2 py-0.5 rounded border',
                  badgeClasses[badgeVariant]
                )}
              >
                {getContentStatusLabel(content.status)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {getContentTypeLabel(content.type)} &middot; {getContentSourceLabel(content.source)}
            </p>
          </div>
        </div>

        {/* Action buttons */}
        {canReview && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowRejectModal(true)}
              disabled={actionLoading}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium',
                'border border-destructive/30 text-destructive',
                'hover:bg-destructive/10 transition-colors',
                'disabled:opacity-50'
              )}
            >
              <XCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Reject</span>
            </button>
            <button
              onClick={handleApprove}
              disabled={actionLoading}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium',
                'bg-emerald-600 text-white',
                'hover:bg-emerald-500 transition-colors',
                'disabled:opacity-50'
              )}
            >
              <CheckCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Approve</span>
            </button>
          </div>
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-6">
        {/* Media Preview — full size so agent can assess properly */}
        <ContentMediaPreview
          type={content.type}
          videoUrl={content.videoUrl}
          posterUrl={content.posterUrl}
          width={content.width}
          height={content.height}
        />

        {/* Revert to Review action — for READY or REJECTED items */}
        {(isReady || content.status === ContentStatus.REJECTED) && (
          <button
            onClick={() => setShowRevertModal(true)}
            disabled={actionLoading}
            className={cn(
              'flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg text-sm font-medium',
              'border border-yellow-500/30 text-yellow-400',
              'hover:bg-yellow-500/10 transition-colors',
              'disabled:opacity-50'
            )}
          >
            <RotateCcw className="h-4 w-4" />
            Back to Review
          </button>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/20 border border-border">
            <Eye className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Views</p>
              <p className="text-sm font-semibold text-foreground">{content.viewCount}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/20 border border-border">
            <Heart className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Likes</p>
              <p className="text-sm font-semibold text-foreground">{content.likesCount}</p>
            </div>
          </div>
          {content.type === ContentType.VIDEO && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/20 border border-border">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Duration</p>
                <p className="text-sm font-semibold text-foreground">
                  {formatVideoDuration(content.durationSec)}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Content Metadata */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Content Details
          </h3>
          <div className="space-y-2">
            {content.caption && (
              <div className="flex items-start gap-2">
                <Tag className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Caption</p>
                  <p className="text-sm text-foreground">{content.caption}</p>
                </div>
              </div>
            )}

            {content.serviceTag && (
              <div className="flex items-start gap-2">
                <Tag className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Service Tag</p>
                  <p className="text-sm text-foreground">{content.serviceTag}</p>
                </div>
              </div>
            )}

            {content.width && content.height && (
              <div className="flex items-start gap-2">
                <Eye className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Resolution</p>
                  <p className="text-sm text-foreground">
                    {content.width} x {content.height}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Created</p>
                <p className="text-sm text-foreground">{formatDateTime(content.createdAt)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* AI Prompt (if AI-generated source) */}
        {content.aiPrompt && (
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              AI Generation
            </h3>
            <div className="p-3 rounded-lg bg-purple-500/5 border border-purple-500/20">
              <p className="text-sm text-foreground">{content.aiPrompt}</p>
            </div>
          </div>
        )}

        {/* Regenerate Video button — for AI-generated content */}
        {canRegenerate && (
          <button
            onClick={() => {
              setRegeneratePrompt(content.aiPrompt || '');
              setShowRegenerateConfirm(true);
            }}
            disabled={actionLoading || regenerateLoading}
            className={cn(
              'flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg text-sm font-medium',
              'border border-amber-500/30 text-amber-400',
              'hover:bg-amber-500/10 transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {regenerateLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Regenerating...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Regenerate Video
              </>
            )}
          </button>
        )}

        {/* Social Import Info */}
        {content.socialImport && (
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <LinkIcon className="h-3.5 w-3.5" />
              Social Import
            </h3>
            <div className="p-3 rounded-lg bg-muted/20 border border-border space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Platform</span>
                <span className="text-sm text-foreground capitalize">
                  {content.socialImport.platform}
                </span>
              </div>
              {content.socialImport.title && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Title</span>
                  <span className="text-sm text-foreground truncate ml-4">
                    {content.socialImport.title}
                  </span>
                </div>
              )}
              {content.socialImport.shareUrl && (
                <a
                  href={content.socialImport.shareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  View original <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* TikTok Import */}
        {isPendingImport && content.socialImport && (
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Download className="h-3.5 w-3.5" />
              TikTok Video Import
            </h3>

            <div className="p-3 rounded-lg bg-purple-500/5 border border-purple-500/20 space-y-3">
              {/* Video ID */}
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Video ID</span>
                <code className="text-foreground bg-muted/30 px-1.5 py-0.5 rounded font-mono text-[11px]">
                  {content.socialImport.externalId}
                </code>
              </div>

              {/* Auto Import Button — primary action */}
              <button
                onClick={handleAutoImport}
                disabled={importLoading || uploadLoading}
                className={cn(
                  'flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  importLoading
                    ? 'bg-purple-600/70 text-white cursor-wait'
                    : 'bg-purple-600 text-white hover:bg-purple-500'
                )}
              >
                {importLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Importing from TikTok...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Auto Import
                  </>
                )}
              </button>

              <p className="text-[11px] text-muted-foreground/70 text-center">
                Downloads the video directly from TikTok servers
              </p>

              {/* Divider */}
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-border" />
                <span className="text-[10px] text-muted-foreground uppercase">or</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Manual Upload — collapsible fallback */}
              <button
                onClick={() => setShowManualUpload(!showManualUpload)}
                className="flex items-center justify-between w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <span>Manual upload (fallback)</span>
                {showManualUpload ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button>

              {showManualUpload && (
                <div className="space-y-3 pt-1">
                  {/* TikTok Share Link */}
                  {content.socialImport.shareUrl && (
                    <a
                      href={content.socialImport.shareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        'flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium w-full',
                        'border border-border text-foreground',
                        'hover:bg-muted/30 transition-colors'
                      )}
                    >
                      <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="truncate">Open TikTok video to download manually</span>
                    </a>
                  )}

                  {/* File Upload Area */}
                  <div
                    onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={cn(
                      'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
                      isDragOver
                        ? 'border-purple-400 bg-purple-500/10'
                        : selectedFile
                          ? 'border-emerald-500/30 bg-emerald-500/5'
                          : 'border-border hover:border-purple-400/50 hover:bg-purple-500/5'
                    )}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".mp4,.mov,video/mp4,video/quicktime"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileSelect(file);
                      }}
                    />
                    {selectedFile ? (
                      <div className="flex flex-col items-center gap-1.5">
                        <FileVideo className="h-8 w-8 text-emerald-400" />
                        <p className="text-sm font-medium text-foreground truncate max-w-full">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(selectedFile.size / (1024 * 1024)).toFixed(1)} MB
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground mt-1"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1.5">
                        <Upload className="h-8 w-8 text-muted-foreground/50" />
                        <p className="text-sm text-muted-foreground">
                          Drag & drop or click to browse
                        </p>
                        <p className="text-xs text-muted-foreground/70">
                          .mp4 or .mov files
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Upload Button */}
                  <button
                    onClick={handleUpload}
                    disabled={!selectedFile || uploadLoading || importLoading}
                    className={cn(
                      'flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
                      selectedFile && !uploadLoading
                        ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                        : 'bg-muted/30 text-muted-foreground cursor-not-allowed'
                    )}
                  >
                    {uploadLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        Upload Video
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Group Session */}
        {content.groupSession && (
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Group Session
            </h3>
            <div className="p-3 rounded-lg bg-muted/20 border border-border">
              <p className="text-sm text-foreground">{content.groupSession.title}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Status: {content.groupSession.status}
              </p>
            </div>
          </div>
        )}

        {/* Owner Info */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Owner
          </h3>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border">
            {content.owner.profileImage ? (
              <img
                src={content.owner.profileImage}
                alt={content.owner.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{content.owner.name}</p>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Mail className="h-3 w-3" />
                <span className="truncate">{content.owner.email}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Review Audit Trail */}
        {content.reviewedBy && (
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Review History
            </h3>
            <div className="p-3 rounded-lg bg-muted/20 border border-border space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Reviewed by</span>
                <span className="text-sm text-foreground">{content.reviewedBy.name}</span>
              </div>
              {content.reviewedAt && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Reviewed at</span>
                  <span className="text-sm text-foreground">
                    {formatDateTime(content.reviewedAt)}
                  </span>
                </div>
              )}
              {content.rejectionReason && (
                <div>
                  <span className="text-xs text-muted-foreground">Rejection reason</span>
                  <p className="text-sm text-destructive mt-1">{content.rejectionReason}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      <RejectReasonModal
        isOpen={showRejectModal}
        onConfirm={handleReject}
        onCancel={() => setShowRejectModal(false)}
        loading={actionLoading}
      />

      {/* Revert Modal */}
      <RejectReasonModal
        isOpen={showRevertModal}
        title="Revert to Review"
        description="This content will be moved back to the review queue for re-evaluation."
        reasonLabel="Reason"
        reasonPlaceholder="Why does this content need re-review?"
        confirmLabel="Revert"
        confirmLoadingLabel="Reverting..."
        confirmVariant="warning"
        onConfirm={handleRevert}
        onCancel={() => setShowRevertModal(false)}
        loading={actionLoading}
      />

      {/* Regenerate Dialog with Prompt Editor */}
      {showRegenerateConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowRegenerateConfirm(false)}>
          <div className="bg-card border border-border rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <RefreshCw className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  Regenerate video for &ldquo;{content.serviceTag}&rdquo;
                </h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Edit the prompt below or leave as-is to reuse the original.
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" />
                AI Prompt
              </label>
              <textarea
                value={regeneratePrompt}
                onChange={(e) => setRegeneratePrompt(e.target.value)}
                rows={5}
                className="w-full rounded-lg border border-border bg-muted/20 px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50 resize-y"
                placeholder="Describe the video you want generated..."
              />
            </div>

            <p className="text-[11px] text-muted-foreground leading-relaxed">
              The current video will be marked as rejected and a new one will be queued. This typically takes 2–5 minutes.
            </p>

            <div className="flex justify-end gap-2 pt-1">
              <button
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowRegenerateConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors disabled:opacity-50"
                disabled={regenerateLoading}
                onClick={handleRegenerate}
              >
                {regenerateLoading ? 'Regenerating...' : 'Regenerate Video'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentDetailPanel;
